/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import type { DelegationRoles } from '@constants/user';
import { hasInheritedOnlyRole, type DelegationRole } from '@utils/user';
import { QuestionStatusStats, UserDelegationStats } from '../../types/initiativeStats';
import { MessageFormFields, MessageType } from './types';
import { ASSIGNMENT } from '@constants/terminology';

export const SUBJECT_MAX_LENGTH = 100;
export const MESSAGE_MAX_LENGTH = 240;

export const isSubjectValid = (subject: string | undefined) => {
  if (subject === undefined) {
    return false;
  }
  const len = subject.length;
  return (len > 0 && len <= SUBJECT_MAX_LENGTH);
}

export const isMessageValid = (message: string | undefined) => {
  if (message === undefined) {
    return false;
  }
  const len = message.length;
  return (len > 0 && len <= MESSAGE_MAX_LENGTH);
}

export const isUserIdsValid = (userIds: string[]) => {
  const len = userIds.length;
  return (len > 0 && userIds[0].length > 0);
}

/** utrvDelegation counts explicit per-metric delegation only; totalDelegation adds initiative roles */
export type DelegationSource = keyof Pick<UserDelegationStats, 'utrvDelegation' | 'totalDelegation'>;

export const getDelegationSource = (showSystemUsers: boolean): DelegationSource =>
  showSystemUsers ? 'totalDelegation' : 'utrvDelegation';

export const DELEGATED_HINTS: Record<DelegationRole | 'both', string> = {
  contributor: `${ASSIGNMENT.roleAccess} contributor`,
  verifier: `${ASSIGNMENT.roleAccess} verifier`,
  both: `${ASSIGNMENT.roleAccess} contributor and verifier`,
};

const isSystemOnly = (user: UserDelegationStats, role: DelegationRole) =>
  Boolean(user.totalDelegation?.[role]) && !user.utrvDelegation?.[role];

// Matches the 'Platform delegated users' toggle: users reached through initiative roles, not explicit delegation
export const getSystemHint = (user: UserDelegationStats, role?: DelegationRole): string | undefined => {
  if (role) {
    return isSystemOnly(user, role) ? DELEGATED_HINTS[role] : undefined;
  }
  const isContributor = isSystemOnly(user, 'contributor');
  const isVerifier = isSystemOnly(user, 'verifier');
  if (isContributor && isVerifier) {
    return DELEGATED_HINTS.both;
  }
  if (isContributor) {
    return DELEGATED_HINTS.contributor;
  }
  if (isVerifier) {
    return DELEGATED_HINTS.verifier;
  }
  return undefined;
};

// getSystemHint's rule from one survey's roles, not tree-wide counts, so it can differ from the bulk modal
export const getSystemHintFromRoles = (roles: DelegationRoles[], role: DelegationRole): string | undefined =>
  hasInheritedOnlyRole(roles, role) ? DELEGATED_HINTS[role] : undefined;

export const getFilteredUsers = (users: UserDelegationStats[], value: QuestionStatusStats, source: DelegationSource) => {
  switch (value) {
    case 'all':
      // System view keeps every user, delegated or not; explicit view needs some direct delegation
      return source === 'totalDelegation'
        ? users
        : users.filter((user) => user[source]?.contributor || user[source]?.verifier);
    case 'created':
      return users.filter((user) => user[source]?.created);
    case 'updated':
      return users.filter((user) => user[source]?.updated || user[source]?.verifierUpdated);
    case 'rejected':
      return users.filter((user) => user[source]?.contributorRejected || user[source]?.verifierRejected);
    case 'verified':
      return users.filter((user) => user[source]?.verified);
  }
}

// DelegationStats statuses are role-scoped: created/updated/contributorRejected count contributor
// work, verifierUpdated/verified/verifierRejected count verifier work
export const getFilteredContributors = (
  users: UserDelegationStats[],
  value: QuestionStatusStats,
  source: DelegationSource,
) => {
  switch (value) {
    case 'all':
      return users.filter((user) => user[source]?.contributor);
    case 'created':
      return users.filter((user) => user[source]?.created);
    case 'updated':
      return users.filter((user) => user[source]?.updated);
    case 'rejected':
      return users.filter((user) => user[source]?.contributorRejected);
    case 'verified':
      return [];
  }
}

export const getFilteredVerifiers = (
  users: UserDelegationStats[],
  value: QuestionStatusStats,
  source: DelegationSource,
) => {
  switch (value) {
    case 'all':
      return users.filter((user) => user[source]?.verifier);
    case 'rejected':
      return users.filter((user) => user[source]?.verifierRejected);
    case 'verified':
      return users.filter((user) => user[source]?.verified);
    case 'updated':
      return users.filter((user) => user[source]?.verifierUpdated);
    case 'created':
      // Unanswered metrics have nothing for a verifier to verify yet
      return [];
  }
}

export const isFormValid = (type: MessageType, form: MessageFormFields): boolean => {

  if (type === MessageType.User) {
    if (!isUserIdsValid(form.userIds)) {
      return false;
    }
  } else {
    if (!isUserIdsValid(form.contributorIds) && !isUserIdsValid(form.verifierIds)) {
      return false;
    }
  }

  if (!isSubjectValid(form.subject)) {
    return false;
  }
  if (!isMessageValid(form.message)) {
    return false;
  }
  return true;
}
